import type { ManagingPromptAction, QuillDelta } from "../types/chat.types";

const CRM_ACTION_BLOT = "crm-action";

function textOp(text: string): Record<string, unknown> {
  return { insert: text };
}

function crmActionOp(action: ManagingPromptAction): Record<string, unknown> {
  const embed: Record<string, unknown> = {
    type: action.type,
    label: action.label ?? action.type,
  };
  if (action.prefill) {
    const prefill: Record<string, unknown> = {};
    if (action.prefill.summary) prefill.summary = action.prefill.summary;
    if (action.prefill.description) prefill.description = action.prefill.description;
    if (action.prefill.dueAt) prefill.dueAt = action.prefill.dueAt;
    if (Object.keys(prefill).length > 0) {
      embed.prefill = prefill;
    }
  }
  return { insert: { [CRM_ACTION_BLOT]: embed } };
}

function endsWithNewline(ops: Record<string, unknown>[]): boolean {
  if (!ops.length) return false;
  return ops[ops.length - 1].insert === "\n";
}

function ensureTrailingNewline(ops: Record<string, unknown>[]): void {
  if (!ops.length || !endsWithNewline(ops)) {
    ops.push(textOp("\n"));
  }
}

function isListLine(line: string): boolean {
  return /^[-*]\s+.+/.test(line);
}

function stripListMarker(line: string): string {
  return line.replace(/^[-*]\s+/, "");
}

function appendPlainLine(ops: Record<string, unknown>[], text: string): void {
  ops.push(textOp(text));
  ops.push(textOp("\n"));
}

function appendSingleLine(ops: Record<string, unknown>[], line: string): void {
  const trimmed = line.trim();
  if (!trimmed) return;
  if (trimmed.startsWith("### ")) {
    ops.push(textOp(trimmed.slice(4)));
    ops.push({ insert: "\n", attributes: { header: 3 } });
    return;
  }
  if (trimmed.startsWith("## ")) {
    ops.push(textOp(trimmed.slice(3)));
    ops.push({ insert: "\n", attributes: { header: 2 } });
    return;
  }
  if (trimmed.startsWith("# ")) {
    ops.push(textOp(trimmed.slice(2)));
    ops.push({ insert: "\n", attributes: { header: 1 } });
    return;
  }
  if (isListLine(trimmed)) {
    ops.push(textOp(stripListMarker(trimmed)));
    ops.push({ insert: "\n", attributes: { list: "bullet" } });
    return;
  }
  appendPlainLine(ops, trimmed);
}

function appendBlockOps(ops: Record<string, unknown>[], block: string): void {
  const lines = block.split("\n");
  if (lines.length === 1) {
    appendSingleLine(ops, lines[0]);
    return;
  }
  if (lines.length > 0 && isListLine(lines[0])) {
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      if (!isListLine(trimmed)) {
        appendPlainLine(ops, trimmed);
        continue;
      }
      ops.push(textOp(stripListMarker(trimmed)));
      ops.push({ insert: "\n", attributes: { list: "bullet" } });
    }
    return;
  }
  for (let i = 0; i < lines.length; i++) {
    if (i > 0) ops.push(textOp("\n"));
    appendSingleLine(ops, lines[i]);
  }
}

function appendMarkdownOps(ops: Record<string, unknown>[], markdown: string): void {
  const blocks = markdown.replace(/\r\n/g, "\n").split(/\n\n+/);
  blocks.forEach((block, i) => {
    if (i > 0) ops.push(textOp("\n"));
    appendBlockOps(ops, block.trim());
  });
}

/** Plain user composer text → minimal Quill Delta. */
export function buildQuillDeltaFromPlainText(text: string): QuillDelta {
  const trimmed = text.trim();
  const ops: Record<string, unknown>[] = trimmed
    ? [textOp(`${trimmed}\n`)]
    : [textOp("\n")];
  return { ops };
}

/** Mirrors typecrm-ai QuillFriendlyResponseBuilder (streaming preview + legacy fallback). */
export function buildQuillDeltaFromMarkdown(
  markdown: string,
  actions: ManagingPromptAction[] = []
): QuillDelta {
  const ops: Record<string, unknown>[] = [];
  const text = markdown?.trim() ?? "";
  if (text) {
    appendMarkdownOps(ops, text);
  }
  for (const action of actions) {
    if (!action?.type) continue;
    if (ops.length && !endsWithNewline(ops)) {
      ops.push(textOp("\n"));
    }
    ops.push(crmActionOp(action));
  }
  ensureTrailingNewline(ops);
  return { ops };
}

/** Prefer server delta; otherwise build from markdown + actions. */
export function resolveMessageContentDelta(
  content: string,
  contentDelta?: QuillDelta | null,
  actions?: ManagingPromptAction[]
): QuillDelta | undefined {
  if (contentDelta?.ops?.length) {
    return contentDelta;
  }
  const trimmed = content?.trim();
  if (!trimmed) {
    return undefined;
  }
  return buildQuillDeltaFromMarkdown(trimmed, actions ?? []);
}
